// Lets a returning SAML/OIDC customer skip retyping their email — a
// still-valid remembered route auto-submits on the /login/sso page. An
// explicit brand click on /login never uses it (see useSsoRoute).
//
// localStorage, not sessionStorage: needs to survive restarts across the
// 90-day TTL, unlike the tab-scoped stash in oauth-challenge-stash.utils.ts.
import { safeReadLocal, safeRemoveLocal, safeWriteLocal } from '@utils/local-storage.utils'

const STORAGE_KEY = 'paretos:hrd_remembered_provider'
const TTL_MS = 90 * 24 * 60 * 60 * 1000

interface StoredRoute {
  provider: string
  savedAt: number
}

// Pure, no side effects — doubles as a lazy useState initializer in
// useSsoRoute (StrictMode runs those twice in dev). Expired entries are
// simply overwritten on the next successful lookup. Extra fields written by
// older builds (e.g. `source`) are ignored.
export const readRememberedProvider = (): string | null => {
  const raw = safeReadLocal(STORAGE_KEY)
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw) as Partial<StoredRoute>
    if (
      typeof parsed.provider !== 'string' ||
      !parsed.provider ||
      typeof parsed.savedAt !== 'number'
    ) {
      return null
    }
    // Bound both ends: a clock-skewed or tampered `savedAt` in the future
    // would otherwise produce a negative age that's always < TTL.
    const age = Date.now() - parsed.savedAt
    if (age < 0 || age >= TTL_MS) {
      return null
    }
    return parsed.provider
  } catch {
    return null
  }
}

export const writeRememberedProvider = (provider: string): void => {
  const entry: StoredRoute = { provider, savedAt: Date.now() }
  safeWriteLocal(STORAGE_KEY, JSON.stringify(entry))
}

export const clearRememberedProvider = (): void => {
  safeRemoveLocal(STORAGE_KEY)
}
